import React, { useState, useRef, useEffect } from "react"
import { AvailabilityRulesData } from "../../../types/availabilityTypes"
import { Badge } from "../../ui/badge"
import { Button } from "../../ui/button"
import { CalendarRequests } from "../../../api/app.service"
import {
  SvgSettings,
  SvgChevronDown,
  SvgEdit,
  SvgCopy,
  SvgTrash,
  SvgFilledInformationCircle,
} from "../../icons"
import { Switch } from "../../ui/switch"
import { useNavigate } from "react-router-dom"
import { useToast } from "../../../hooks/useToast"

interface AvailabilityCardsProps {
  availability: any
  onDeleteClick: (availability: AvailabilityRulesData) => void
  setShowOnOffModal: (value: boolean) => void
  services: any
  user: any
  getAvailabilityRules: () => void
}

const AvailabilityCards: React.FC<AvailabilityCardsProps> = ({
  availability,
  onDeleteClick,
  setShowOnOffModal,
  services,
  user,
  getAvailabilityRules,
}) => {
  const { toast } = useToast()
  const [openDropdown, setOpenDropdown] = useState(false)
  const [isActive, setIsActive] = useState(availability.status === "active")
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const serviceCount = services.filter(
    (service: any) =>
      service.service_details?.availability_rule_id === availability?.id &&
      service.service_details?.status === "active"
  ).length

  const displayStatus =
    availability?.status.toLowerCase() === "inactive"
      ? "Paused"
      : availability?.status.charAt(0).toUpperCase() +
        availability?.status.slice(1)

  const handleSwitchChange = (checked: boolean) => {
    setIsActive(checked)
    toggleStatus(checked)
  }

  const toggleStatus = async (checked: boolean) => {
    try {
      await CalendarRequests.editAvailabilitySettings({
        user_id: parseInt(user.user_id),
        availability_rule_id: availability?.id,
        settings: {
          status: checked ? "active" : "inactive",
        },
      })
      getAvailabilityRules()
    } catch (error: any) {
      console.error(error)
      toast.error(error?.response?.data?.message || "Failed to update status")
      getAvailabilityRules()
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openDropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [openDropdown])

  return (
    <div
      className={`cursor-pointer w-full sm:w-[450px] md:w-[325px] lg:w-[450px] rounded-xl main-shadow flex flex-col hover:transform hover:scale-105 transition-transform duration-300 shadow-custom relative bg-white ${
        openDropdown ? "z-50" : ""
      }`}
      onClick={() =>
        navigate(`/account-settings/availability/${availability.id}`)
      }
    >
      <div
        className="absolute top-0 left-0 w-full h-4 rounded-t-xl"
        style={{
          backgroundColor:
            availability.status === "inactive" ? "#727679" : "#4750F5",
        }}
      />
      <div className="pt-8 px-8 pb-4 flex flex-col space-y-2">
        <p className="font-bold text-lg">{availability.name}</p>

        <div
          className="flex items-center justify-between w-full relative"
          ref={dropdownRef}
        >
          <p className="text-sm text-blue-600 font-semibold">
            {serviceCount} Service{serviceCount !== 1 ? "s" : ""} -{" "}
            {displayStatus}
          </p>

          <div className="flex items-center space-x-1">
            {availability.is_default === true && (
              <Badge variant="success" className="text-xs">
                Default
              </Badge>
            )}

            <Button
              variant="ghost"
              className="flex items-center space-x-1 px-2 py-1"
              onClick={(e) => {
                e.stopPropagation()
                setOpenDropdown((prev) => !prev)
              }}
            >
              <SvgSettings fill="black" />
              <SvgChevronDown
                className={`transition-transform duration-200 ${
                  openDropdown ? "rotate-180" : ""
                }`}
              />
            </Button>
          </div>

          {openDropdown && (
            <div className="absolute right-0 top-full mt-2 bg-white border border-grayCloud rounded-lg shadow-lg z-[999] p-2 w-max max-w-xs">
              <div className="flex flex-col space-y-1">
                <Button
                  variant="ghost"
                  className="w-48 flex items-center px-4 py-2 text-sm whitespace-nowrap justify-start"
                  onClick={() =>
                    navigate(
                      `/account-settings/availability/${availability.id}`
                    )
                  }
                >
                  <SvgEdit className="inline mr-2" /> View/Edit
                </Button>
                <Button
                  variant="ghost"
                  className="w-48 flex items-center px-4 py-2 text-sm whitespace-nowrap justify-start"
                  onClick={(e) => {
                    e.stopPropagation()
                    const duplicatedAvailability = {
                      ...availability,
                      id: undefined,
                      is_default: false,
                      name: `${availability.name} Copy`,
                    }
                    navigate(`/account-settings/availability`, {
                      state: {
                        availability: duplicatedAvailability,
                        services,
                        duplicate: true,
                      },
                    })
                  }}
                >
                  <SvgCopy className="inline mr-2" /> Duplicate
                </Button>
                {!availability.is_default && (
                  <Button
                    variant="ghost"
                    className="w-48 flex items-center px-4 py-2 text-sm whitespace-nowrap justify-start"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteClick(availability)
                      setOpenDropdown(false)
                    }}
                  >
                    <SvgTrash className="inline mr-2" /> Delete
                  </Button>
                )}
                <div
                  className="w-48 flex items-center px-4 py-2 text-sm space-x-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="ml-0">On/off</span>
                  <Switch
                    checked={isActive}
                    onCheckedChange={handleSwitchChange}
                  />
                  <Button
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowOnOffModal(true)
                    }}
                    variant="ghost"
                    size="icon"
                    className="hidden md:flex"
                  >
                    <SvgFilledInformationCircle className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AvailabilityCards
